// src/components/DropshippingBookkeeping.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Download, 
  Printer, 
  FileSpreadsheet,
  Calendar,
  Calculator,
  BookOpen,
  TrendingUp,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';
import './DropshippingBookkeeping.css';

const DropshippingBookkeeping = ({ user, userProfile }) => {
  const [ledgers, setLedgers] = useState([]);
  const [currentLedger, setCurrentLedger] = useState(null);
  const [incomeData, setIncomeData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 편집 상태
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showNewIncomeForm, setShowNewIncomeForm] = useState(false);
  const [showNewExpenseForm, setShowNewExpenseForm] = useState(false);
  const [showNewLedgerForm, setShowNewLedgerForm] = useState(false);

  // 폼 데이터
  const [newIncomeEntry, setNewIncomeEntry] = useState({
    transactionDate: new Date().toISOString().split('T')[0],
    customerName: '',
    itemName: '',
    quantity: 1,
    unitPrice: 0,
    memo: ''
  });

  const [newExpenseEntry, setNewExpenseEntry] = useState({
    transactionDate: new Date().toISOString().split('T')[0],
    vendorName: '',
    itemName: '',
    amount: 0,
    accountType: '',
    memo: ''
  });

  const [newLedgerData, setNewLedgerData] = useState({
    title: '드롭시핑 간편장부',
    businessYear: new Date().getFullYear(),
    businessMonth: new Date().getMonth() + 1,
    notes: ''
  });

  const accountTypes = [
    '매입비', '임차료', '광고선전비', '통신비', '소모품비', 
    '수수료', '교육훈련비', '차량비', '감가상각비', '기타경비'
  ];

  // 서버 URL
  const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';

  // 초기 데이터 로드
  useEffect(() => {
    if (user?.uid) {
      loadLedgers();
    }
  }, [user]);

  // 현재 장부가 변경될 때 상세 데이터 로드
  useEffect(() => {
    if (currentLedger?.id) {
      loadLedgerDetail(currentLedger.id);
    }
  }, [currentLedger]);

  // API 호출 헬퍼 함수
  const apiCall = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${serverUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || '서버 오류가 발생했습니다.');
      }

      return data;
    } catch (error) {
      console.error('API 호출 오류:', error);
      throw error;
    }
  };

  // 장부 목록 로드
  const loadLedgers = async () => {
    try {
      setLoading(true);
      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      const data = await apiCall('/api/ledgers', {
        method: 'POST',
        body: JSON.stringify({ userInfo })
      });

      setLedgers(data.ledgers || []);
      
      // 첫 번째 장부를 자동 선택
      if (data.ledgers && data.ledgers.length > 0) {
        setCurrentLedger(data.ledgers[0]);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 장부 상세 로드
  const loadLedgerDetail = async (ledgerId) => {
    try {
      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      const data = await apiCall(`/api/ledgers/${ledgerId}`, {
        method: 'POST',
        body: JSON.stringify({ userInfo })
      });

      setCurrentLedger(data.ledger);
      setIncomeData(data.income || []);
      setExpenseData(data.expense || []);

    } catch (error) {
      setError(error.message);
    }
  };

  // 새 장부 생성
  const createLedger = async () => {
    try {
      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      const data = await apiCall('/api/ledgers/create', {
        method: 'POST',
        body: JSON.stringify({ 
          userInfo,
          ledgerData: newLedgerData
        })
      });

      setNewLedgerData({
        title: '드롭시핑 간편장부',
        businessYear: new Date().getFullYear(),
        businessMonth: new Date().getMonth() + 1,
        notes: ''
      });
      setShowNewLedgerForm(false);
      
      await loadLedgers();
      alert(data.message);

    } catch (error) {
      alert(`오류: ${error.message}`);
    }
  };

  // 수입 항목 추가
  const addIncomeEntry = async () => {
    try {
      if (!currentLedger?.id) {
        alert('장부를 먼저 선택해주세요.');
        return;
      }

      if (!newIncomeEntry.transactionDate || !newIncomeEntry.customerName || !newIncomeEntry.itemName) {
        alert('필수 항목을 모두 입력해주세요.');
        return;
      }

      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      await apiCall(`/api/ledgers/${currentLedger.id}/income`, {
        method: 'POST',
        body: JSON.stringify({ 
          userInfo,
          incomeData: newIncomeEntry
        })
      });

      setNewIncomeEntry({
        transactionDate: new Date().toISOString().split('T')[0],
        customerName: '',
        itemName: '',
        quantity: 1,
        unitPrice: 0,
        memo: ''
      });
      setShowNewIncomeForm(false);
      
      await loadLedgerDetail(currentLedger.id);

    } catch (error) {
      alert(`오류: ${error.message}`);
    }
  };

  // 지출 항목 추가
  const addExpenseEntry = async () => {
    try {
      if (!currentLedger?.id) {
        alert('장부를 먼저 선택해주세요.');
        return;
      }

      if (!newExpenseEntry.transactionDate || !newExpenseEntry.vendorName || !newExpenseEntry.itemName) {
        alert('필수 항목을 모두 입력해주세요.');
        return;
      }

      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      await apiCall(`/api/ledgers/${currentLedger.id}/expense`, {
        method: 'POST',
        body: JSON.stringify({ 
          userInfo,
          expenseData: newExpenseEntry
        })
      });

      setNewExpenseEntry({
        transactionDate: new Date().toISOString().split('T')[0],
        vendorName: '',
        itemName: '',
        amount: 0,
        accountType: '',
        memo: ''
      });
      setShowNewExpenseForm(false);
      
      await loadLedgerDetail(currentLedger.id);

    } catch (error) {
      alert(`오류: ${error.message}`);
    }
  };

  // 수입 항목 수정
  const updateIncomeEntry = async (incomeId, updatedData) => {
    try {
      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      await apiCall(`/api/ledgers/${currentLedger.id}/income/${incomeId}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          userInfo,
          incomeData: updatedData
        })
      });

      setEditingIncome(null);
      await loadLedgerDetail(currentLedger.id);

    } catch (error) {
      alert(`오류: ${error.message}`);
    }
  };

  // 지출 항목 수정
  const updateExpenseEntry = async (expenseId, updatedData) => {
    try {
      const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
      };

      await apiCall(`/api/ledgers/${currentLedger.id}/expense/${expenseId}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          userInfo,
          expenseData: updatedData
        })
      });

      setEditingExpense(null);
      await loadLedgerDetail(currentLedger.id);

    } catch (error) {
      alert(`오류: ${error.message}`);
    }
  };

  // 수입 항목 삭제 
    const deleteIncomeEntry = async (incomeId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
        const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
        };

        await apiCall(`/api/ledgers/${currentLedger.id}/income/${incomeId}`, {
        method: 'DELETE',
        body: JSON.stringify({ userInfo })
        });

        await loadLedgerDetail(currentLedger.id);

    } catch (error) {
        alert(`오류: ${error.message}`);
    }
    };

  // 지출 항목 삭제
    const deleteExpenseEntry = async (expenseId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
        const userInfo = {
        uid: user.uid,
        email: user.email,
        nickname: userProfile?.nickname
        };

        await apiCall(`/api/ledgers/${currentLedger.id}/expense/${expenseId}`, {
        method: 'DELETE',
        body: JSON.stringify({ userInfo })
        });

        await loadLedgerDetail(currentLedger.id);

    } catch (error) {
        alert(`오류: ${error.message}`);
    }
    };

  // Excel 다운로드
  const downloadExcel = () => {
    if (!currentLedger) {
      alert('장부를 선택해주세요.');
      return;
    }

    const wb = XLSX.utils.book_new();

    // 수입 시트
    const incomeWs = XLSX.utils.json_to_sheet(
      incomeData.map(item => ({
        '거래일자': item.transaction_date,
        '거래처': item.customer_name,
        '품목': item.item_name,
        '수량': item.quantity,
        '단가': item.unit_price,
        '금액': item.amount,
        '비고': item.memo || ''
      }))
    );
    XLSX.utils.book_append_sheet(wb, incomeWs, '수입');

    // 지출 시트
    const expenseWs = XLSX.utils.json_to_sheet(
      expenseData.map(item => ({
        '지출일자': item.transaction_date,
        '거래처': item.vendor_name,
        '품목': item.item_name,
        '금액': item.amount,
        '계정과목': item.account_type,
        '비고': item.memo || ''
      }))
    );
    XLSX.utils.book_append_sheet(wb, expenseWs, '지출');

    // 요약 시트
    const summaryData = [
      { '구분': '총 수입', '금액': currentLedger.total_income },
      { '구분': '총 지출', '금액': currentLedger.total_expense },
      { '구분': '순이익', '금액': currentLedger.net_profit }
    ];
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, '요약');

    const fileName = `간편장부_${currentLedger.business_year}년${currentLedger.business_month}월_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // 인쇄
  const printLedger = () => {
    window.print();
  };

  // 숫자 포맷팅
  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="bookkeeping-loading">
        <div className="loading-spinner"></div>
        <p>장부 데이터를 불러오는 중...</p>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="bookkeeping-error">
        <p>오류: {error}</p>
        <button onClick={loadLedgers} className="retry-btn">
          <RefreshCw size={16} />
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="bookkeeping-container">
      {/* 헤더 */}
      <div className="bookkeeping-header">
        <div className="header-left">
          <h2>드롭시핑 간편장부</h2>
          <p>개인사업자 매출/지출 관리</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowNewLedgerForm(true)}
            className="btn btn-primary"
          >
            <Plus size={16} />
            새 장부
          </button>
          {currentLedger && (
            <>
              <button onClick={downloadExcel} className="btn btn-secondary">
                <FileSpreadsheet size={16} />
                Excel
              </button>
              <button onClick={printLedger} className="btn btn-secondary">
                <Printer size={16} />
                인쇄
              </button>
            </>
          )}
        </div>
      </div>

      {/* 장부 선택 */}
      {ledgers.length > 0 && (
        <div className="ledger-selector">
          <label>장부 선택:</label>
          <select 
            value={currentLedger?.id || ''}
            onChange={(e) => {
              const selectedLedger = ledgers.find(l => l.id === parseInt(e.target.value));
              setCurrentLedger(selectedLedger);
            }}
            className="ledger-select"
          >
            <option value="">장부를 선택하세요</option>
            {ledgers.map(ledger => (
              <option key={ledger.id} value={ledger.id}>
                {ledger.title} ({ledger.business_year}년 {ledger.business_month}월)
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 장부가 없는 경우 */}
      {ledgers.length === 0 && (
        <div className="no-ledgers">
          <BookOpen size={48} />
          <h3>생성된 장부가 없습니다</h3>
          <p>첫 번째 간편장부를 생성해보세요.</p>
          <button 
            onClick={() => setShowNewLedgerForm(true)}
            className="btn btn-primary"
          >
            <Plus size={16} />
            새 장부 만들기
          </button>
        </div>
      )}

      {/* 현재 장부 정보 */}
      {currentLedger && (
        <div className="current-ledger-info">
          <div className="ledger-summary">
            <div className="summary-item">
              <TrendingUp className="summary-icon income" />
              <div>
                <span className="summary-label">총 수입</span>
                <span className="summary-value income">
                  {formatNumber(currentLedger.total_income)}원
                </span>
              </div>
            </div>
            <div className="summary-item">
              <Calculator className="summary-icon expense" />
              <div>
                <span className="summary-label">총 지출</span>
                <span className="summary-value expense">
                  {formatNumber(currentLedger.total_expense)}원
                </span>
              </div>
            </div>
            <div className="summary-item">
              <BookOpen className="summary-icon profit" />
              <div>
                <span className="summary-label">순이익</span>
                <span className={`summary-value ${currentLedger.net_profit >= 0 ? 'profit' : 'loss'}`}>
                  {formatNumber(currentLedger.net_profit)}원
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 수입 섹션 */}
      {currentLedger && (
        <div className="ledger-section">
          <div className="section-header">
            <h3>💰 수입금액 기록부 (매출)</h3>
            <button 
              onClick={() => setShowNewIncomeForm(true)}
              className="btn btn-success"
            >
              <Plus size={16} />
              수입 추가
            </button>
          </div>

          {/* 새 수입 입력 폼 */}
          {showNewIncomeForm && (
            <div className="entry-form income-form">
              <div className="form-grid">
                <input
                  type="date"
                  value={newIncomeEntry.transactionDate}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, transactionDate: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="거래처명"
                  value={newIncomeEntry.customerName}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, customerName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="품목명"
                  value={newIncomeEntry.itemName}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, itemName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="number"
                  placeholder="수량"
                  value={newIncomeEntry.quantity}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, quantity: parseInt(e.target.value) || 0})}
                  className="form-input"
                />
                <input
                  type="number"
                  placeholder="단가"
                  value={newIncomeEntry.unitPrice}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, unitPrice: parseInt(e.target.value) || 0})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="금액 (자동계산)"
                  value={formatNumber(newIncomeEntry.quantity * newIncomeEntry.unitPrice)}
                  readOnly
                  className="form-input readonly"
                />
                <input
                  type="text"
                  placeholder="비고"
                  value={newIncomeEntry.memo}
                  onChange={(e) => setNewIncomeEntry({...newIncomeEntry, memo: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button onClick={addIncomeEntry} className="btn btn-success">
                  <Save size={16} />
                  저장
                </button>
                <button onClick={() => setShowNewIncomeForm(false)} className="btn btn-secondary">
                  <X size={16} />
                  취소
                </button>
              </div>
            </div>
          )}

          {/* 수입 테이블 */}
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>거래일자</th>
                  <th>거래처</th>
                  <th>품목</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>금액</th>
                  <th>비고</th>
                  <th className="no-print">관리</th>
                </tr>
              </thead>
              <tbody>
                {incomeData.map((entry) => (
                  <IncomeRow
                    key={entry.id}
                    entry={entry}
                    isEditing={editingIncome === entry.id}
                    onEdit={() => setEditingIncome(entry.id)}
                    onSave={(updatedData) => updateIncomeEntry(entry.id, updatedData)}
                    onCancel={() => setEditingIncome(null)}
                    onDelete={() => deleteIncomeEntry(entry.id)}
                    formatNumber={formatNumber}
                  />
                ))}
                <tr className="total-row">
                  <td colSpan="5"><strong>월 합계</strong></td>
                  <td><strong>{formatNumber(currentLedger.total_income)}</strong></td>
                  <td></td>
                  <td className="no-print"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 지출 섹션 */}
      {currentLedger && (
        <div className="ledger-section">
          <div className="section-header">
            <h3>💸 필요경비 기록부 (지출)</h3>
            <button 
              onClick={() => setShowNewExpenseForm(true)}
              className="btn btn-danger"
            >
              <Plus size={16} />
              지출 추가
            </button>
          </div>

          {/* 새 지출 입력 폼 */}
          {showNewExpenseForm && (
            <div className="entry-form expense-form">
              <div className="form-grid">
                <input
                  type="date"
                  value={newExpenseEntry.transactionDate}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, transactionDate: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="거래처명"
                  value={newExpenseEntry.vendorName}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, vendorName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="품목명"
                  value={newExpenseEntry.itemName}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, itemName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="number"
                  placeholder="금액"
                  value={newExpenseEntry.amount}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, amount: parseInt(e.target.value) || 0})}
                  className="form-input"
                />
                <select
                  value={newExpenseEntry.accountType}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, accountType: e.target.value})}
                  className="form-input"
                >
                  <option value="">계정과목 선택</option>
                  {accountTypes.map(account => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="비고"
                  value={newExpenseEntry.memo}
                  onChange={(e) => setNewExpenseEntry({...newExpenseEntry, memo: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-actions">
                <button onClick={addExpenseEntry} className="btn btn-danger">
                  <Save size={16} />
                  저장
                </button>
                <button onClick={() => setShowNewExpenseForm(false)} className="btn btn-secondary">
                  <X size={16} />
                  취소
                </button>
              </div>
            </div>
          )}

          {/* 지출 테이블 */}
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>지출일자</th>
                  <th>거래처</th>
                  <th>품목</th>
                  <th>금액</th>
                  <th>계정과목</th>
                  <th>비고</th>
                  <th className="no-print">관리</th>
                </tr>
              </thead>
              <tbody>
                {expenseData.map((entry) => (
                  <ExpenseRow
                    key={entry.id}
                    entry={entry}
                    isEditing={editingExpense === entry.id}
                    onEdit={() => setEditingExpense(entry.id)}
                    onSave={(updatedData) => updateExpenseEntry(entry.id, updatedData)}
                    onCancel={() => setEditingExpense(null)}
                    onDelete={() => deleteExpenseEntry(entry.id)}
                    accountTypes={accountTypes}
                    formatNumber={formatNumber}
                  />
                ))}
                <tr className="total-row">
                  <td colSpan="3"><strong>월 합계</strong></td>
                  <td><strong>{formatNumber(currentLedger.total_expense)}</strong></td>
                  <td colSpan="3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 새 장부 생성 모달 */}
      {showNewLedgerForm && (
        <div className="modal-overlay" onClick={() => setShowNewLedgerForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>새 장부 생성</h3>
              <button onClick={() => setShowNewLedgerForm(false)} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>장부 제목</label>
                <input
                  type="text"
                  value={newLedgerData.title}
                  onChange={(e) => setNewLedgerData({...newLedgerData, title: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>사업연도</label>
                  <input
                    type="number"
                    value={newLedgerData.businessYear}
                    onChange={(e) => setNewLedgerData({...newLedgerData, businessYear: parseInt(e.target.value)})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>사업월</label>
                  <select
                    value={newLedgerData.businessMonth}
                    onChange={(e) => setNewLedgerData({...newLedgerData, businessMonth: parseInt(e.target.value)})}
                    className="form-input"
                  >
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i+1} value={i+1}>{i+1}월</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>메모</label>
                <textarea
                  value={newLedgerData.notes}
                  onChange={(e) => setNewLedgerData({...newLedgerData, notes: e.target.value})}
                  className="form-input"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={createLedger} className="btn btn-primary">
                생성
              </button>
              <button onClick={() => setShowNewLedgerForm(false)} className="btn btn-secondary">
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 수입 행 컴포넌트
const IncomeRow = ({ entry, isEditing, onEdit, onSave, onCancel, onDelete, formatNumber }) => {
  const [editData, setEditData] = useState({
    transactionDate: entry.transaction_date,
    customerName: entry.customer_name,
    itemName: entry.item_name,
    quantity: entry.quantity,
    unitPrice: entry.unit_price,
    memo: entry.memo || ''
  });

  if (isEditing) {
    return (
      <tr className="editing-row">
        <td>
          <input
            type="date"
            value={editData.transactionDate}
            onChange={(e) => setEditData({...editData, transactionDate: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="text"
            value={editData.customerName}
            onChange={(e) => setEditData({...editData, customerName: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="text"
            value={editData.itemName}
            onChange={(e) => setEditData({...editData, itemName: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="number"
            value={editData.quantity}
            onChange={(e) => setEditData({...editData, quantity: parseInt(e.target.value) || 0})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="number"
            value={editData.unitPrice}
            onChange={(e) => setEditData({...editData, unitPrice: parseInt(e.target.value) || 0})}
            className="edit-input"
          />
        </td>
        <td>{formatNumber(editData.quantity * editData.unitPrice)}</td>
        <td>
          <input
            type="text"
            value={editData.memo}
            onChange={(e) => setEditData({...editData, memo: e.target.value})}
            className="edit-input"
          />
        </td>
        <td className="no-print">
          <div className="action-buttons">
            <button onClick={() => onSave(editData)} className="btn-action save">
              <Save size={14} />
            </button>
            <button onClick={onCancel} className="btn-action cancel">
              <X size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{entry.transaction_date}</td>
      <td>{entry.customer_name}</td>
      <td>{entry.item_name}</td>
      <td>{entry.quantity}</td>
      <td>{formatNumber(entry.unit_price)}</td>
      <td className="amount">{formatNumber(entry.amount)}</td>
      <td>{entry.memo}</td>
      <td className="no-print">
        <div className="action-buttons">
          <button onClick={onEdit} className="btn-action edit">
            <Edit size={14} />
          </button>
          <button onClick={onDelete} className="btn-action delete">
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

// 지출 행 컴포넌트
const ExpenseRow = ({ entry, isEditing, onEdit, onSave, onCancel, onDelete, accountTypes, formatNumber }) => {
  const [editData, setEditData] = useState({
    transactionDate: entry.transaction_date,
    vendorName: entry.vendor_name,
    itemName: entry.item_name,
    amount: entry.amount,
    accountType: entry.account_type,
    memo: entry.memo || ''
  });

  if (isEditing) {
    return (
      <tr className="editing-row">
        <td>
          <input
            type="date"
            value={editData.transactionDate}
            onChange={(e) => setEditData({...editData, transactionDate: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="text"
            value={editData.vendorName}
            onChange={(e) => setEditData({...editData, vendorName: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="text"
            value={editData.itemName}
            onChange={(e) => setEditData({...editData, itemName: e.target.value})}
            className="edit-input"
          />
        </td>
        <td>
          <input
            type="number"
            value={editData.amount}
            onChange={(e) => setEditData({...editData, amount: parseInt(e.target.value) || 0})}
            className="edit-input"
          />
        </td>
        <td>
          <select
            value={editData.accountType}
            onChange={(e) => setEditData({...editData, accountType: e.target.value})}
            className="edit-input"
          >
            {accountTypes.map(account => (
              <option key={account} value={account}>{account}</option>
            ))}
          </select>
        </td>
        <td>
          <input
            type="text"
            value={editData.memo}
            onChange={(e) => setEditData({...editData, memo: e.target.value})}
            className="edit-input"
          />
        </td>
        <td className="no-print">
          <div className="action-buttons">
            <button onClick={() => onSave(editData)} className="btn-action save">
              <Save size={14} />
            </button>
            <button onClick={onCancel} className="btn-action cancel">
              <X size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{entry.transaction_date}</td>
      <td>{entry.vendor_name}</td>
      <td>{entry.item_name}</td>
      <td className="amount">{formatNumber(entry.amount)}</td>
      <td>{entry.account_type}</td>
      <td>{entry.memo}</td>
      <td className="no-print">
        <div className="action-buttons">
          <button onClick={onEdit} className="btn-action edit">
            <Edit size={14} />
          </button>
          <button onClick={onDelete} className="btn-action delete">
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default DropshippingBookkeeping;